import React, { useState } from 'react';
import { Player, Payment, Expense } from '../types';

interface PlayerManagerProps {
  players: Player[];
  payments: Payment[];
  expenses: Expense[];
  config: { tournamentFee: number; transportTotal: number; teamName: string };
}

export const PlayerManager: React.FC<PlayerManagerProps> = ({ players, payments, expenses, config }) => {
  const [isOpen, setIsOpen] = useState(false);

  const activePlayerCount = players.filter(p => p.isActive).length;
  const transportTotal = config.transportTotal;
  const transportPerHead = activePlayerCount > 0 ? Math.round(transportTotal / activePlayerCount) : 0;

  // Get player payment status
  const getPlayerPaymentInfo = (playerId: string) => {
    const tournamentPaid = payments
      .filter(p => p.playerId === playerId && p.categoryId === 'tournament' && p.isPaid)
      .reduce((sum, p) => sum + p.amount, 0);

    const transportPaid = payments
      .filter(p => p.playerId === playerId && p.categoryId === 'transport' && p.isPaid)
      .reduce((sum, p) => sum + p.amount, 0);

    const foodExpenses = expenses?.filter(e => e.categoryId === 'food') || [];
    const totalFoodExpenses = foodExpenses.reduce((sum, e) => sum + e.amount, 0);
    const foodPerHead = activePlayerCount > 0 ? Math.round(totalFoodExpenses / activePlayerCount) : 0;
    const foodSpent = foodExpenses
      .filter(e => e.paidBy === playerId)
      .reduce((sum, e) => sum + e.amount, 0);

    const tournamentOwed = config.tournamentFee;
    const tournamentBalance = tournamentOwed - tournamentPaid;
    const transportBalance = transportPerHead - transportPaid;
    const foodBalance = foodPerHead - foodSpent;
    const totalBalance = tournamentBalance + transportBalance + foodBalance;

    return {
      tournamentPaid,
      transportPaid,
      foodSpent,
      totalBalance,
      status: totalBalance === 0 ? 'Settled' : totalBalance > 0 ? 'Needs to Pay' : 'To Receive'
    };
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
      {/* Accordion Header */}
      <div
        className="accordion-header px-6 py-4 cursor-pointer flex justify-between items-center transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg font-semibold">Player Overview</span>
          {!isOpen && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-sm text-gray-300">
                {activePlayerCount} active players
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Accordion Content */}
      {isOpen && (
        <div>
          <table className="w-full border-collapse">
            <thead>
              <tr className="table-header">
                <th className="px-4 py-3 text-left font-medium">Player Name</th>
                <th className="px-4 py-3 text-center font-medium">Tournament</th>
                <th className="px-4 py-3 text-center font-medium">Transport</th>
                <th className="px-4 py-3 text-center font-medium">Food Spent</th>
                <th className="px-4 py-3 text-center font-medium">Balance</th>
                <th className="px-4 py-3 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {players.filter(p => p.isActive).map((player) => {
                const paymentInfo = getPlayerPaymentInfo(player.id);
                return (
                  <tr key={player.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{player.name}</td>
                    <td className="px-4 py-2 text-center text-sm">
                      <span className={paymentInfo.tournamentPaid >= config.tournamentFee ? 'text-green-600' : 'text-red-600'}>
                        ₹{paymentInfo.tournamentPaid.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      <span className={paymentInfo.transportPaid >= transportPerHead ? 'text-green-600' : 'text-red-600'}>
                        ₹{paymentInfo.transportPaid.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      <span className="text-blue-600">
                        ₹{paymentInfo.foodSpent.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      <span className={paymentInfo.totalBalance > 0 ? 'text-red-600' : paymentInfo.totalBalance < 0 ? 'text-green-600' : 'text-gray-600'}>
                        {paymentInfo.totalBalance > 0 ? '+' : ''}₹{paymentInfo.totalBalance.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        paymentInfo.status === 'Settled' ? 'bg-green-100 text-green-800' :
                        paymentInfo.status === 'Needs to Pay' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {paymentInfo.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};